import React, { useState, useEffect } from 'react';
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Grid,
  FormGroup,
  FormControlLabel,
  Radio,
  RadioGroup,
  CircularProgress,
  Card,
  CardContent,
  Link
} from '@mui/material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

function App() {
  const [keyword, setKeyword] = useState('');
  const [sites, setSites] = useState([]);
  const [selectedSite, setSelectedSite] = useState('');
  const [allResults, setAllResults] = useState([]);
  const [filteredResults, setFilteredResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchSites();
  }, []);

  useEffect(() => {
    filterResults();
  }, [selectedSite, allResults]);

  const fetchSites = async () => {
    try {
      const response = await axios.get(`${API_URL}/api/sites`);
      setSites(response.data.sites);
    } catch (error) {
      setError('获取站点列表失败');
      console.error('获取站点列表失败:', error);
    }
  };

  const filterResults = () => {
    if (!selectedSite) {
      setFilteredResults(allResults);
    } else {
      const selectedSiteData = sites.find(site => site.id === selectedSite);
      setFilteredResults(allResults.filter(result => {
        try {
          // 解析结果URL获取域名
          const resultDomain = new URL(result.url).hostname.replace(/^www\./, '');
          // 移除站点域名开头的www.
          const siteDomain = selectedSiteData.domain.replace(/^www\./, '');
          // 检查域名是否匹配
          return resultDomain === siteDomain;
        } catch (error) {
          console.error('URL解析错误:', error);
          return false;
        }
      }));
    }
  };

  const handleSearch = async () => {
    if (!keyword.trim()) {
      setError('请输入搜索关键词');
      return;
    }

    setLoading(true);
    setError(null);
    setAllResults([]);
    setFilteredResults([]);

    try {
      const response = await axios.post(`${API_URL}/api/search`, {
        keyword: keyword.trim()
      });

      setAllResults(response.data.results);
    } catch (error) {
      setError('搜索失败，请稍后重试');
      console.error('搜索失败:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSiteChange = (event) => {
    setSelectedSite(event.target.value);
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ my: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          资源聚合搜索系统
        </Typography>

        <Box sx={{ mb: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <TextField
                fullWidth
                label="搜索关键词"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <Button
                fullWidth
                variant="contained"
                onClick={handleSearch}
                disabled={loading}
                sx={{ height: '56px' }}
              >
                {loading ? <CircularProgress size={24} /> : '搜索'}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ mb: 3 }}>
          <Typography variant="h6" gutterBottom>
            选择搜索站点
          </Typography>
          <RadioGroup
            row
            value={selectedSite}
            onChange={handleSiteChange}
          >
            <FormControlLabel
              value=""
              control={<Radio />}
              label={`全部 (${allResults.length})`}
            />
            {sites.map((site) => {
              const siteResults = allResults.filter(
                result => result.site === site.domain
              ).length;
              return (
                <FormControlLabel
                  key={site.id}
                  value={site.id}
                  control={<Radio />}
                  label={`${site.name} (${siteResults})`}
                />
              );
            })}
          </RadioGroup>
        </Box>

        {error && (
          <Typography color="error" sx={{ mb: 2 }}>
            {error}
          </Typography>
        )}

        <Grid container spacing={2}>
          {filteredResults.map((result, index) => (
            <Grid item xs={12} key={index}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    <Link href={result.url} target="_blank" rel="noopener">
                      {result.title}
                    </Link>
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {result.content}
                  </Typography>
                  <Typography variant="caption" display="block" sx={{ mt: 1 }}>
                    来源: {result.siteName}
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>
    </Container>
  );
}

export default App;
